import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Bed, Bath, Square, ArrowRight } from "lucide-react";
import { properties } from "@/data/properties";

const Properties = () => {
  return (
    <section id="properties" className="py-24 bg-background">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="text-accent text-sm tracking-[0.2em] uppercase font-medium">
            Featured Listings
          </span>
          <h2 className="text-4xl md:text-5xl font-display font-semibold text-foreground mt-4 mb-6">
            Exclusive Properties
          </h2>
          <p className="text-muted-foreground text-lg">
            Discover handpicked luxury homes and investment opportunities in Chicago's 
            most desirable neighborhoods.
          </p>
        </motion.div>

        {/* Properties Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {properties.map((property, index) => (
            <motion.div
              key={property.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Link to={`/property/${property.slug}`} className="group block cursor-pointer">
                {/* Image */}
                <div className="aspect-[4/3] bg-muted rounded-sm overflow-hidden mb-6 relative">
                  <img 
                    src={property.image} 
                    alt={`${property.title} - ${property.beds} bed ${property.baths} bath luxury property in ${property.address}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  />
                  <div className="absolute top-4 left-4 bg-accent text-accent-foreground px-3 py-1 text-sm font-semibold rounded-sm">
                    {property.price}
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-display font-semibold text-foreground group-hover:text-accent transition-colors">
                  {property.title}
                </h3>
                <p className="text-muted-foreground text-sm mt-1 mb-4">{property.address}</p>

                {/* Features */}
                <div className="flex items-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Bed className="h-4 w-4" />
                    <span>{property.beds} Beds</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Bath className="h-4 w-4" />
                    <span>{property.baths} Baths</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Square className="h-4 w-4" />
                    <span>{property.sqft} sqft</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link to="/properties">
            <Button variant="default" size="lg">
              View All Properties
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Properties;